/**
 * BaziCash Consulta - Sistema de Consulta de Saldo
 * Versão Black Friday 2025
 */

const BaziCashConsulta = {
  // === CONFIGURAÇÃO ===
  API_BASE_URL: '/apps/bazicash',
  customerEmail: null,
  customerLoggedIn: false,
  cachedBalance: null,
  cacheTimestamp: null,
  CACHE_DURATION: 60000, // 60 segundos
  MAX_RETRIES: 3,
  RETRY_DELAYS: [1000, 2000, 3000], // ms

  // === INICIALIZAÇÃO ===
  init() {
    console.log('[BaziCash Consulta] Inicializando...');

    // Verificar se está logado
    this.customerLoggedIn = window.bazicashCustomerLoggedIn || false;
    this.customerEmail = window.bazicashCustomerEmail || null;

    if (this.customerLoggedIn) {
      console.log('[BaziCash Consulta] Cliente logado, carregando saldo...');
      this.loadBalance();
    } else {
      console.log('[BaziCash Consulta] Cliente não logado');
    }
  },

  // === MÉTODOS PÚBLICOS ===

  /**
   * Carrega o saldo do cliente e atualiza a UI
   */
  async loadBalance() {
    console.log('[BaziCash Consulta] Carregando saldo...');

    // Mostrar loading
    this.showLoading();

    try {
      // Verificar cache
      if (this.isCacheValid()) {
        console.log('[BaziCash Consulta] Usando saldo do cache');
        this.displayBalance(this.cachedBalance);
        return;
      }

      // Buscar saldo da API
      const balance = await this.fetchBalance();

      if (balance !== null) {
        // Armazenar no cache
        this.cachedBalance = balance;
        this.cacheTimestamp = Date.now();

        // Exibir na UI
        this.displayBalance(balance);
      } else {
        throw new Error('Não foi possível obter o saldo');
      }

    } catch (error) {
      console.error('[BaziCash Consulta] Erro ao carregar saldo:', error);
      this.showError(error.message || 'Erro ao carregar saldo. Tente novamente.');
    }
  },

  /**
   * Busca o saldo via API
   * @returns {Promise<number|null>} Saldo em BaziCash ou null em caso de erro
   */
  async fetchBalance() {
    console.log('[BaziCash Consulta] Buscando saldo da API...');

    const result = await this.callAPI('balance');

    if (result.success && result.data) {
      const balance = result.data.available_balance || 0;
      console.log('[BaziCash Consulta] Saldo obtido:', balance, 'BZ');
      return balance;
    }

    return null;
  },

  /**
   * Exibe o saldo na UI
   * @param {number} balance - Saldo em BaziCash
   */
  displayBalance(balance) {
    console.log('[BaziCash Consulta] Exibindo saldo:', balance);

    // Ocultar loading
    this.hideLoading();

    // Mostrar card de saldo
    const balanceCard = document.getElementById('bazicashBalanceCard');
    const infoSection = document.getElementById('bazicashInfoSection');

    if (balanceCard) {
      balanceCard.style.display = 'block';

      // Atualizar valor
      const amountElement = balanceCard.querySelector('.bazicash-amount');
      if (amountElement) {
        amountElement.textContent = this.formatCurrency(balance);
        this.adjustFontSizeForLongNumbers(amountElement);
      }

      // Atualizar conversão
      const currencyValueElement = document.getElementById('bazicashCurrencyValue');
      if (currencyValueElement) {
        const reais = balance / 10;
        currencyValueElement.textContent = `R$ ${this.formatCurrency(reais)}`;
        this.adjustFontSizeForLongNumbers(currencyValueElement);
      }

      // Adicionar animação de entrada
      setTimeout(() => {
        balanceCard.classList.add('bazicash-balance-card--loaded');
      }, 100);
    }

    // Mostrar seção de informações
    if (infoSection) {
      infoSection.style.display = 'block';
    }
  },

  /**
   * Formata número como moeda brasileira
   * @param {number} value - Valor a formatar
   * @returns {string} Valor formatado
   */
  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  },

  // === MÉTODOS PRIVADOS - UI ===

  /**
   * Mostra estado de loading
   */
  showLoading() {
    const loading = document.getElementById('bazicashLoading');
    const balanceCard = document.getElementById('bazicashBalanceCard');
    const errorCard = document.getElementById('bazicashErrorCard');
    const infoSection = document.getElementById('bazicashInfoSection');

    if (loading) loading.style.display = 'block';
    if (balanceCard) balanceCard.style.display = 'none';
    if (errorCard) errorCard.style.display = 'none';
    if (infoSection) infoSection.style.display = 'none';
  },

  /**
   * Oculta estado de loading
   */
  hideLoading() {
    const loading = document.getElementById('bazicashLoading');
    if (loading) loading.style.display = 'none';
  },

  /**
   * Mostra mensagem de erro
   * @param {string} message - Mensagem de erro
   */
  showError(message) {
    console.error('[BaziCash Consulta] Exibindo erro:', message);

    this.hideLoading();

    const balanceCard = document.getElementById('bazicashBalanceCard');
    const errorCard = document.getElementById('bazicashErrorCard');
    const errorMessage = document.getElementById('bazicashErrorMessage');
    const infoSection = document.getElementById('bazicashInfoSection');

    if (balanceCard) balanceCard.style.display = 'none';
    if (infoSection) infoSection.style.display = 'none';

    if (errorCard) {
      errorCard.style.display = 'block';

      if (errorMessage) {
        errorMessage.textContent = message;
      }
    }
  },

  // === MÉTODOS PRIVADOS - API ===

  /**
   * Wrapper para chamadas à API com retry logic
   * @param {string} endpoint - Endpoint da API
   * @param {object} options - Opções do fetch
   * @returns {Promise<object>} Resposta da API
   */
  async callAPI(endpoint, options = {}) {
    const url = `${this.API_BASE_URL}/${endpoint}`;

    console.log(`[BaziCash Consulta] Chamando API: ${url}`);

    let lastError = null;

    // Tentar com retry
    for (let attempt = 0; attempt < this.MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(url, {
          method: options.method || 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...options.headers
          },
          body: options.body || null,
          credentials: 'same-origin'
        });

        console.log(`[BaziCash Consulta] Resposta (tentativa ${attempt + 1}):`, response.status);

        // Sucesso
        if (response.ok) {
          const data = await response.json();
          return {
            success: true,
            data: data,
            message: data.message || 'Sucesso'
          };
        }

        // Erro específico
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || this.getErrorMessage(response.status);

        // Não fazer retry em alguns erros
        if ([401, 403, 404, 422].includes(response.status)) {
          console.error(`[BaziCash Consulta] Erro não recuperável (${response.status}):`, errorMessage);
          return {
            success: false,
            data: null,
            message: errorMessage
          };
        }

        lastError = new Error(errorMessage);
        console.warn(`[BaziCash Consulta] Erro na tentativa ${attempt + 1}:`, errorMessage);

      } catch (error) {
        lastError = error;
        console.error(`[BaziCash Consulta] Exceção na tentativa ${attempt + 1}:`, error);
      }

      // Aguardar antes de tentar novamente
      if (attempt < this.MAX_RETRIES - 1) {
        const delay = this.RETRY_DELAYS[attempt];
        console.log(`[BaziCash Consulta] Aguardando ${delay}ms antes de tentar novamente...`);
        await this.sleep(delay);
      }
    }

    // Todas as tentativas falharam
    console.error('[BaziCash Consulta] Todas as tentativas falharam');
    throw lastError || new Error('Erro ao comunicar com a API');
  },

  /**
   * Retorna mensagem de erro amigável baseada no status HTTP
   * @param {number} status - Status HTTP
   * @returns {string} Mensagem de erro
   */
  getErrorMessage(status) {
    const messages = {
      400: 'Dados inválidos',
      401: 'Você precisa estar logado para consultar seu saldo',
      403: 'Acesso negado',
      404: 'Saldo não encontrado. Entre em contato com o suporte.',
      422: 'Erro ao processar os dados',
      500: 'Erro no servidor. Tente novamente mais tarde.',
      502: 'Serviço temporariamente indisponível',
      503: 'Serviço em manutenção'
    };

    return messages[status] || `Erro ${status}. Tente novamente mais tarde.`;
  },

  /**
   * Função auxiliar para sleep
   * @param {number} ms - Milissegundos
   * @returns {Promise}
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  },

  // === CACHE ===

  /**
   * Verifica se o cache ainda é válido
   * @returns {boolean}
   */
  isCacheValid() {
    if (!this.cachedBalance || !this.cacheTimestamp) {
      return false;
    }

    const now = Date.now();
    const elapsed = now - this.cacheTimestamp;

    return elapsed < this.CACHE_DURATION;
  },

  /**
   * Ajusta o tamanho da fonte para números muito longos
   * @param {HTMLElement} element - Elemento com o número
   */
  adjustFontSizeForLongNumbers(element) {
    if (!element) return;

    const text = element.textContent || '';
    const length = text.replace(/[^\d]/g, '').length; // Contar apenas dígitos

    // Reset para valor padrão
    element.style.fontSize = '';

    // Ajustar baseado no número de dígitos
    if (length >= 10) {
      // Números muito grandes (10+ dígitos)
      element.style.fontSize = window.innerWidth <= 749 ? 'clamp(20px, 5vw, 28px)' : 'clamp(32px, 4vw, 42px)';
    } else if (length >= 8) {
      // Números grandes (8-9 dígitos)
      element.style.fontSize = window.innerWidth <= 749 ? 'clamp(24px, 6vw, 32px)' : 'clamp(38px, 5vw, 48px)';
    } else if (length >= 6) {
      // Números médios (6-7 dígitos)
      element.style.fontSize = window.innerWidth <= 749 ? 'clamp(28px, 7vw, 36px)' : 'clamp(42px, 5.5vw, 52px)';
    }
    // Números pequenos mantêm tamanho padrão definido no CSS
  }
};

// === INICIALIZAÇÃO AUTOMÁTICA ===
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    BaziCashConsulta.init();
  });
} else {
  BaziCashConsulta.init();
}

// === MELHORIAS DE UX MOBILE ===
// Prevenir zoom duplo ao clicar em botões em iOS
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const modal = document.getElementById('bazicash-modal');
    if (modal) {
      modal.querySelectorAll('button, a').forEach(element => {
        element.style.touchAction = 'manipulation';
      });
    }
  });
}

// Expor globalmente para debug
window.BaziCashConsulta = BaziCashConsulta;
